import { ConfirmDialogVariant, FormDialogState } from 'interfaces/formDialog';
import { useFormDialog } from 'providers/FormDialogProvider';
import { MouseEvent, useState } from 'react';
import { CUSTOM_SCHEME_PATH, CUSTOM_SCHEME_PREFIX, MethodUrlLike } from 'services/api-constants';
import { useClient } from 'urql';
import { FormType, TableDialog } from 'generated/graphql';
import { useFeatures } from './useFeatures';
import useHandleFileDownload from './useHandleFileDownload';
import useHandleLink from './useHandleLink';
import useHandleMethod from './useHandleMethod';
import useHandleTableDialog from './useHandleTableDialog';
import useLogSentryWithContext from './useLogSentryWithContext';
import { SnackbarVariant, useSnackbar } from './useSnackBar';

// Utility function to detect if modifier keys are pressed (for opening in new tab)
const isModifiedClick = (event?: MouseEvent): boolean => {
  if (!event) return false;
  return event.ctrlKey || event.metaKey || event.shiftKey;
};

const isCustomScheme = (url: string): boolean =>
  Object.values(CUSTOM_SCHEME_PREFIX).some((scheme) => url.startsWith(scheme));

/**
 * Parse method: URLs manually so we don't rely on new URL() for non-http schemes
 * (pathname/searchParams can be inconsistent across environments).
 * e.g. "method:switchProfile?id=123" -> { pathname: "switchProfile", searchParams }
 */
const parseMethodUrl = (url: string): MethodUrlLike | null => {
  if (!url.startsWith(CUSTOM_SCHEME_PREFIX.Method)) return null;
  const afterScheme = url.slice(CUSTOM_SCHEME_PREFIX.Method.length);
  const [pathname, search] = afterScheme.split('?');
  const searchParams = new URLSearchParams(search ?? '');
  return {
    pathname: (pathname ?? '').trim(),
    searchParams,
    href: url,
  };
};

/** Parse dialog: URLs manually (new URL() can be unreliable for custom schemes). */
const parseDialogUrl = (url: string): MethodUrlLike | null => {
  if (!url.startsWith(CUSTOM_SCHEME_PREFIX.Dialog)) return null;
  const afterScheme = url.slice(CUSTOM_SCHEME_PREFIX.Dialog.length);
  const [pathname, search] = afterScheme.split('?');
  const searchParams = new URLSearchParams(search ?? '');
  return {
    pathname: (pathname ?? '').trim(),
    searchParams,
    href: url,
  };
};

interface CustomSchemeFields {
  confirmDialogVariant?: ConfirmDialogVariant;
  /** Forward the link's search into the dialog (QuotesSearch). */
  includeSearch?: boolean;
}
export interface ActionResponse {
  /** Refetches what an action may have changed. Runs after a method, a mutation or a submitted form. */
  onMutate?: () => void;
  /** Runs when a mutation comes back with an error. */
  onError?: () => void;
  /** Answers the server's method:expandRow. */
  onExpandRow?: () => void;
  /** Answers the server's method:notify. */
  onNotify?: () => void;
  /** Reports whether a button's action has hidden its row (CompleteType.Hide). */
  onHide?: (hidden: boolean) => void;
  /** Reports whether a button's row waits on a Stale action's change to land. */
  onStale?: (stale: boolean) => void;
}
interface CustomSchemeActions {
  openFormDialog?: (state: FormDialogState) => void;
  onClose?: () => void;
}

const handleForm = (
  urlEntity: MethodUrlLike,
  actions: CustomSchemeActions,
  fields: CustomSchemeFields,
  logError: (error: any, context?: {}) => void,
  openFormDialog: (state: FormDialogState) => void,
  response: ActionResponse,
) => {
  const formType = urlEntity.searchParams.get('type') as FormType;
  const profileID = urlEntity.searchParams.get('profileID') ?? undefined;
  let ids: string[] = [];
  try {
    const idsJson = urlEntity.searchParams.get('ids');
    if (idsJson) {
      ids = JSON.parse(idsJson) as string[];
    }
  } catch (e) {
    logError(e, { message: 'Failed to parse IDs from URL:' });
    ids = [];
  }

  let eTags: string[] = [];
  try {
    const eTagsJson = urlEntity.searchParams.get('eTags');
    if (eTagsJson) {
      eTags = JSON.parse(eTagsJson) as string[];
    }
  } catch (e) {
    logError(e, { message: 'Failed to parse eTags from URL:' });
    eTags = [];
  }
  const search = fields.includeSearch ? urlEntity.searchParams.get('search') : null;
  (actions.openFormDialog ?? openFormDialog)({
    open: true,
    editIDs: ids,
    eTags: eTags,
    type: formType,
    ...(profileID ? { profileID } : {}),
    ...(search ? { search } : {}),
    ...(fields.confirmDialogVariant ? { confirmDialogVariant: fields.confirmDialogVariant } : {}),
    onSubmit: response.onMutate,
  });
};

const useHandleCustomSchemeAndQuery = (response: ActionResponse = {}) => {
  const { onMutate } = response;
  const { isEnabled } = useFeatures();
  const isQuotesSearchEnabled = isEnabled('QuotesSearch');

  const client = useClient();
  const [isLoading, setIsLoading] = useState(false);
  const handleMethod = useHandleMethod();
  const handleLink = useHandleLink();
  const handleTableDialog = useHandleTableDialog();
  const { handleFileDownload, LoadingOverlay: FileDownloadLoadingOverlay } = useHandleFileDownload(setIsLoading);
  const logError = useLogSentryWithContext();

  const [paymentDetailsDialogShipmentId, setPaymentDetailsDialogShipmentId] = useState('');
  const [paymentDetailsDialogOpen, setPaymentDetailsDialogOpen] = useState(false);
  const [tableDialog, setTableDialog] = useState<TableDialog>();

  const { showSnackbar } = useSnackbar();
  const { openFormDialog } = useFormDialog();

  const handleSetErrorMessage = (message: string) => {
    showSnackbar({ message, color: 'error', autoHideDuration: 3000 }, SnackbarVariant.ERROR);
  };
  const handleCustomScheme = (url: string, actions: CustomSchemeActions, fields: CustomSchemeFields) => {
    // Handle file: URLs first since they don't need URL parsing and may contain invalid URL characters
    if (url.startsWith(CUSTOM_SCHEME_PREFIX.File)) {
      handleFileDownload(url.substring(CUSTOM_SCHEME_PREFIX.File.length));
      return;
    }

    // Parse method:/dialog: URLs manually so pathname/searchParams work reliably (new URL() is inconsistent for custom schemes)
    const methodParsed = parseMethodUrl(url);
    const dialogParsed = parseDialogUrl(url);
    const urlEntity: URL | MethodUrlLike | null =
      methodParsed ??
      dialogParsed ??
      (() => {
        try {
          return new URL(url);
        } catch (error) {
          logError(error, { message: 'Failed to construct URL', url });
          return null;
        }
      })();

    if (!urlEntity) return;

    if (
      urlEntity.pathname.startsWith(CUSTOM_SCHEME_PATH.ShipmentTable) ||
      urlEntity.pathname.startsWith(CUSTOM_SCHEME_PATH.TableDialog)
    ) {
      handleTableDialog(urlEntity as URL, setTableDialog, handleSetErrorMessage);
    }

    if (url.startsWith(CUSTOM_SCHEME_PREFIX.Dialog) && urlEntity.pathname.startsWith(CUSTOM_SCHEME_PATH.Form)) {
      handleForm(
        urlEntity as MethodUrlLike,
        actions,
        { ...fields, includeSearch: isQuotesSearchEnabled },
        logError,
        openFormDialog,
        response,
      );
    }

    if (url.startsWith(CUSTOM_SCHEME_PREFIX.Method)) {
      handleMethod(urlEntity, setIsLoading, handleSetErrorMessage, onMutate);
    }
    if (urlEntity.pathname.startsWith(CUSTOM_SCHEME_PATH.PayNow)) {
      setPaymentDetailsDialogOpen(true);
      setPaymentDetailsDialogShipmentId(urlEntity.searchParams.get('shipmentID') || '');
    }

    if (urlEntity.pathname.startsWith(CUSTOM_SCHEME_PATH.ExpandRow)) {
      response.onExpandRow?.();
    }

    if (
      url.startsWith(CUSTOM_SCHEME_PREFIX.Method) &&
      urlEntity.pathname.trim().startsWith(CUSTOM_SCHEME_PATH.Notify)
    ) {
      response.onNotify?.();
    }
  };

  // Runs mutation returned via the query field of a button action
  const handleQuery = async (query: string, onClose?: () => void) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      // Trimmed because server-built queries can carry leading whitespace from
      // their raw literals, which is insignificant GraphQL but fails startsWith.
      const mutation = query.trim();
      if (!mutation.startsWith('mutation')) {
        handleSetErrorMessage('An error occurred while processing the query.');
        logError('Mutation query expected, but received different operation type.');
        return;
      }

      const result = await client.mutation(mutation, undefined).toPromise();
      if (result.error?.message) {
        response.onError?.();
        return result;
      }

      if (onClose) onClose();

      if (onMutate) onMutate();
    } catch {
      handleSetErrorMessage('An error occurred while processing the query.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSchemeOrQuery = (
    url = '',
    query = '',
    actions: CustomSchemeActions = {},
    fields: CustomSchemeFields = {},
    event?: MouseEvent,
  ) => {
    // Prefer custom scheme (method:/dialog:) when present so backend action URLs are always handled
    if (url.length > 0 && isCustomScheme(url)) {
      handleCustomScheme(url, actions, fields);
      return null;
    }
    if (query.length > 0) {
      return handleQuery(query, actions.onClose);
    }

    if (url.length > 0) {
      const openInNewTab = isModifiedClick(event);
      handleLink(url, openInNewTab);
    }

    return null;
  };

  return {
    handleSchemeOrQuery,
    isLoading,
    fileDownloadLoadingOverlay: FileDownloadLoadingOverlay,
    paymentDetails: {
      open: paymentDetailsDialogOpen,
      id: paymentDetailsDialogShipmentId,
      onClose: () => setPaymentDetailsDialogOpen(false),
    },
    tableDialog: {
      content: tableDialog,
      clear: () => setTableDialog(undefined),
    },
  };
};

export default useHandleCustomSchemeAndQuery;
